<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl">Settlements</h1>
    <Button variant="outline" @click="exportToCSV" :disabled="allSettlements.length === 0" title="Download CSV">
      <Download class="h-4 w-4" />
      Download
    </Button>
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <SettlementsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="settlementsTableColumns"
      :data="paginatedSettlements"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
    />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import { LAYUP_API_GET_ITEMS_LIMIT } from '@/services/layupApi';
import { fetchSettlements, type SettlementsServiceGetReportRequest } from '@/services/settlements';
import { currency, displayDate } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import { Download } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref } from 'vue';
import type { ModelSettlementReport } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import SettlementsControls from '../settlements-controls/SettlementsControls.vue';
import { settlementsTableColumns } from './SettlementsTableColumns';

const isLoading = ref(true);
const settlementsParams = ref<SettlementsServiceGetReportRequest>({ limit: LAYUP_API_GET_ITEMS_LIMIT, sort: '-batch' });
const previousParams = ref<SettlementsServiceGetReportRequest>({ ...settlementsParams.value });
const allSettlements = ref<ModelSettlementReport[]>([]);

const paginatedSettlements = computed(() => {
  return allSettlements.value?.slice((pageNumber.value - 1) * pageSize.value, pageNumber.value * pageSize.value);
});

const totalPages = ref(1);
const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

onMounted(async () => onUpdateFilter(settlementsParams.value, true));

const onUpdateFilter = async (params: SettlementsServiceGetReportRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  allSettlements.value = [];
  isLoading.value = true;

  try {
    const settlementsResponse = await fetchSettlements(params);

    allSettlements.value = settlementsResponse?.settlements ?? [];
    totalPages.value = Math.ceil(allSettlements.value.length / pageSize.value);
    totalCount.value = allSettlements.value.length;
    pageNumber.value = 1;

    addMetric('settlements-table', { status: 'success', channel: 'merchant', method: 'fetchSettlements' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settlements-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('settlements-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = (value: DateRange): void => {
  settlementsParams.value.fromDate = dayjs(`${value.start?.year}-${value.start?.month}-${value.start?.day}`)
    .startOf('day')
    .toISOString();
  settlementsParams.value.toDate = dayjs(`${value.end?.year}-${value.end?.month}-${value.end?.day}`)
    .endOf('day')
    .toISOString();

  onUpdateFilter(settlementsParams.value);
};

const onClearFilters = (): void => {
  settlementsParams.value = { limit: LAYUP_API_GET_ITEMS_LIMIT, sort: '-batch' };
  onUpdateFilter(settlementsParams.value);
};

const onSearchTermChange = (value: string): void => {
  settlementsParams.value.search = value.toUpperCase();
  onUpdateFilter(settlementsParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};

const exportToCSV = () => {
  if (allSettlements.value.length === 0) {
    console.warn('No data available for export');
    return;
  }

  const csvContent = generateCSV(allSettlements.value);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `settlements-${dayjs().format('YYYY-MM-DD')}.csv`);
  link.click();

  URL.revokeObjectURL(url);
};

const generateCSV = (data: ModelSettlementReport[]): string => {
  if (!data) return '';
  const headerMap: { [Property in keyof ModelSettlementReport]: string } = {
    batch: 'Settlement Batch',
    reference: 'Settlement Reference',
    settledAt: 'Date Settled',
    amount: 'Settlement Amount',
    batchType: 'Batch Type',
  };

  const headers = Object.keys(headerMap) as Array<keyof ModelSettlementReport>;
  const headerTitles = headers.map((header) => headerMap[header]);

  const rows = data.map((row) => {
    return headers
      .map((header) => {
        let value = row[header] || '';
        if (header === 'amount') value = currency(value);
        if (header === 'settledAt' && typeof value === 'string') value = displayDate(value);
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(',');
  });

  return [headerTitles.join(','), ...rows].join('\n');
};
</script>
